import { useSidebar } from '@solinkcorp/solink-ui/components/ui/sidebar';
import React, { Suspense, useEffect } from 'react';
import { useControllerState } from 'solox';
import { DAY_PART_SIDEBAR_COLLAPSED_WIDTH } from '../../constants';
import type { Controller } from '../../Controller';
import { useSelectedLayoutId } from '../../hooks/useSelectedLayoutId';
import {
  useCustomerDefaultSDMLayout,
  useServiceDisplayLayouts,
} from '../../hooks/useServiceDisplayLayouts';
import type { ServiceDisplayLayoutSnapshot } from '../../layout/types';
import styles from '../../sdm.module.css';
import type { ServiceDisplayEventsController } from '../../ServiceDisplayEventsController';
import { DayPartSidebar } from '../DayParts/DayPartSidebar';
import { DayPartSidebarTrigger } from '../DayParts/DayPartSideBarTrigger';
import WidgetDialog from '../WidgetDialog';
import Grid from './Grid';
import Header from './Header/Header';
import SDMAdoptionTracker from './SDMAdoptionTracker';
import SDMSkeletonGrid from './SDMSkeletonGrid';
import SDMSkeletonHeader from './SDMSkeletonHeader';

interface MonitorProps {
  timezoneId: string;
  controller: Controller;
  eventsController: ServiceDisplayEventsController;
  editMode?: boolean;
  dayPartIndex: number;
  onDayPartIndexChange: (index: number) => void;
  sidebarWidth: number;
  onSidebarWidthChange: (width: number) => void;
  canEdit: boolean;
  isStandalone: boolean;
}

/**
 * Inner component that uses the sidebar context
 */
const Monitor: React.FC<MonitorProps> = (props) => {
  const {
    controller,
    eventsController,
    editMode,
    dayPartIndex,
    onDayPartIndexChange,
    sidebarWidth,
    onSidebarWidthChange,
    canEdit,
    isStandalone,
  } = props;
  const { data: layouts } = useServiceDisplayLayouts();
  const editing = useControllerState(controller.state, (state) => !!state.editingLayout);
  const dialogOpen = useControllerState(controller.state, (state) => !!state.configuringWidgetId);
  const { data: defaultSdmLayoutId } = useCustomerDefaultSDMLayout();
  const { state, open, toggleSidebar } = useSidebar();
  const [urlLayoutId, setSelectedLayoutId] = useSelectedLayoutId();
  const selectedLayoutId = useControllerState(controller.state, (s) => s.selectedLayout?.id);

  useEffect(() => {
    if (layouts) {
      controller.setLayouts(layouts, defaultSdmLayoutId, urlLayoutId);
    }
  }, [layouts, controller, defaultSdmLayoutId, urlLayoutId]);

  // Mirror the controller's resolved selection back to the URL so refreshes
  // and shared links land on the same layout.
  useEffect(() => {
    if (selectedLayoutId && urlLayoutId !== selectedLayoutId) {
      setSelectedLayoutId(selectedLayoutId);
    }
  }, [selectedLayoutId, urlLayoutId, setSelectedLayoutId]);

  const sidebarColWidth = state === 'collapsed' ? DAY_PART_SIDEBAR_COLLAPSED_WIDTH : sidebarWidth;

  return (
    <div className={`${styles.container} tw`}>
      {dialogOpen && <WidgetDialog controller={controller} />}
      <Suspense fallback={null}>
        <SDMAdoptionTracker layouts={layouts} />
      </Suspense>
      <Suspense fallback={<SDMSkeletonHeader />}>
        <Header controller={controller} canEdit={canEdit} />
      </Suspense>
      <Suspense fallback={<SDMSkeletonGrid />}>
        <div
          data-testid="main-grid"
          className="grid h-full min-h-0 gap-[1px] transition-[grid-template-columns] duration-200 ease-linear"
          style={{
            gridTemplateColumns: editing ? `auto ${sidebarColWidth}px` : '1fr',
          }}
        >
          <Grid
            editMode={editMode}
            editing={editing}
            controller={controller}
            eventsController={eventsController}
            isStandalone={isStandalone}
          />
          {editing && (
            <div
              className="relative h-full min-h-0 overflow-hidden"
              data-sdm-sidebar-column
              data-testid="sidebar-column"
            >
              {!open ? (
                <DayPartSidebarTrigger open={open} toggleSidebar={toggleSidebar} />
              ) : (
                <DayPartSidebar
                  open={open}
                  toggleSidebar={toggleSidebar}
                  dayPartIndex={dayPartIndex}
                  onDayPartIndexChange={onDayPartIndexChange}
                  sidebarWidth={sidebarWidth}
                  onSidebarWidthChange={onSidebarWidthChange}
                  layouts={(layouts ?? []) as ServiceDisplayLayoutSnapshot[]}
                />
              )}
            </div>
          )}
        </div>
      </Suspense>
    </div>
  );
};

export default React.memo(Monitor);
